import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, forwardRef, ViewChild } from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor, Validator, AbstractControl, ValidationErrors, NG_VALIDATORS, NgForm } from '@angular/forms';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { InputTextModule } from 'primeng/inputtext';
import { LocationService } from '../../services/location.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'brt-location-selector',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    AutoCompleteModule, InputTextModule,
    MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule
  ],
  templateUrl: './location-selector.component.html',
  styleUrl: './location-selector.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LocationSelectorComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => LocationSelectorComponent),
      multi: true
    }
  ]
})
export class LocationSelectorComponent implements ControlValueAccessor, Validator {
  constructor(private locationService: LocationService) { }

  @ViewChild('locationForm') locationForm: NgForm;
  private onValidatorChange: () => void = () => {};

  @Input('phone-required') phoneRequired: boolean;
  @Input('address-required') addressRequired: boolean = true;
  @Input() required: boolean;
  @Input('show-title') showTitle: boolean = true;
  @Input('show-phone') showPhone: boolean = true;
  @Input('postcode-readonly') postcodeReadonly: boolean;
  @Input('postcode-changeable') postcodeChangeable: boolean;
  @Input('address-lookup') addressLookup: boolean;
  @Output() postcodeChange = new EventEmitter<void>();
  @Input() lines: number = 1;
  @Input() editMode = true;
  value: any;

  // Some mobile browsers select the whole value (or otherwise move the caret to the
  // end) when a pre-filled field is tapped. Put the caret at the start once focus
  // settles so people can prefix the road name with a house number without having
  // to manually move the cursor first.
  roadFocused(event: FocusEvent) {
    const input = event.target as HTMLInputElement;
    setTimeout(() => input.setSelectionRange(0, 0));
  }

  writeValue(value: any) {
    this.value = { ...(this.value || {}), ...(value || {}) };
    this.value.AddressLines = this.value.AddressLines || [];
    for (var i = this.value.AddressLines.length; i < this.lines; i++)
      this.value.AddressLines.push('');
    if (this.showPhone && this.value.Phone) {
      this.value._phone = value.Phone.replace(/^\+44|^0/, '').replace(/\D/g, '').slice(0, 10);
    }
  }
  registerOnChange(fn: any) { this.onChange = fn; }
  registerOnTouched(fn: any) { this.onTouched = fn; }
  onChange = (_: any) => { };
  onTouched = () => { };
  valueChanged() {
    if (this.showPhone)
      this.value.Phone = `+44${this.value._phone?.replace(/^0/, '').replace(/\D/g, '').slice(0, 10)}`;
    setTimeout(() => {
      this.onChange(this.value);
      this.onValidatorChange();
    });
  }

  postcodes: string[];
  async searchPostcode(query: string, outcodes = false) {
    this.postcodes = await this.locationService.searchPostcode(query, outcodes);
  }
  fetchHighway?: boolean;
  async onSelectPostcode(event: any) {
    // Legacy postcode-only flow. This is not rendered in BritPOS; BritPOS uses
    // the PAF-backed /api/location/addresses endpoint through lookupAddresses().
    var location = await this.locationService.getPostcode(event.value);
    if (location) {
      this.value = Object.assign(this.value, location);
      this.fetchHighway = true;
      try { this.value.Highway = await this.locationService.getRoadName(location.Postcode); }
      finally { this.fetchHighway = false; }
    }
    this.valueChanged();
  }

  addressResults: any[] = [];
  addressLookupBusy = false;
  addressLookupMessage = '';

  async lookupAddresses() {
    const postcode = (this.value?.Postcode || '').replace(/\s+/g, '').toUpperCase();
    if (!/^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/.test(postcode)) {
      this.addressResults = [];
      this.addressLookupMessage = 'Enter a full UK postcode.';
      return;
    }
    this.addressLookupBusy = true;
    this.addressLookupMessage = '';
    try {
      this.addressResults = await this.locationService.findAddresses(postcode);
      if (!this.addressResults.length) this.addressLookupMessage = 'No addresses found.';
    } catch {
      this.addressResults = [];
      this.addressLookupMessage = 'Address lookup failed. Please try again.';
    } finally {
      this.addressLookupBusy = false;
    }
  }

  selectLookupAddress(address: any) {
    const phone = this.value?._phone;
    const addressLines = [...(address.AddressLines || [])];
    // Put the full premises line (house number + street, e.g. "56 Howard Agne
    // Close") into Road rather than the bare thoroughfare, and shift the
    // remaining lines up (Address Line 2 -> Address Line 1). This reduces
    // mistakes, especially with Ideal Postcodes results where the thoroughfare
    // alone drops the premises number.
    let highway = '';
    const roadIndex = addressLines.findIndex(_ => !!_?.trim());
    if (roadIndex >= 0) highway = addressLines.splice(roadIndex, 1)[0];
    if (!highway?.trim()) highway = address.Highway;
    this.value = { ...this.value, ...address, Highway: highway, AddressLines: addressLines };
    while (this.value.AddressLines.length < this.lines) this.value.AddressLines.push('');
    this.value._phone = phone;
    this.addressResults = [];
    this.addressLookupMessage = '';
    this.valueChanged();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    let errors = null as ValidationErrors | null;
    if (this.required) {
      if (!this.value?.Postcode) errors = { ...errors, postcodeRequired: true };
      if (this.addressRequired && !this.value?.AddressLines?.some(_ => !!_))
        errors = { ...errors, addressLinesRequired: true };
      if (this.phoneRequired && !this.value?._phone) errors = { ...errors, phoneRequired: true };
      if (!this.value?.Highway?.trim()) errors = { ...errors, highwayRequired: true };
    }
    // Validate phone pattern
    if (this.showPhone && this.value?._phone && !this.value._phone.match(/^0?\d{10}$/))
      errors = { ...errors, phoneInvalid: true };
    // Check internal form validity (pattern validators, etc.)
    if (this.locationForm?.invalid) {
      errors = { ...errors, internalFormInvalid: true };
    }
    return errors;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }
}
